import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  FormControl,
  MenuItem,
  Box,
  Typography,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import CustomTextField from "../../../../components/ui/Textfield";
import CustomSelect from "../../../../components/ui/Select";
import { tokens } from "../../../../theme";
import { Close } from "@mui/icons-material";
import apiService from "../../../../service/Api";
import { useParams } from "react-router-dom";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";

interface IStreamData {
  streamTitle: string;
  description: string;
  privacySetting: "Public" | "Private" | "Unlisted";
  thumbnailUrl: string;
  scheduledStartTime: string;
}

export interface IGenerateYouTubeLinkDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedChannel?: { name?: string } | null;
  streamData?: {
    title: string;
    date: string;

    start: string;
    address_one: string;
    address_two?: string;
    suburb: string;
    city: string;
    venue_name?: string | null;
    service?: {
      venue?: {
        name: string;
      };
    };
    event_details?: {
      venue?: {
        name: string;
      };
    };
    youtube_stream_link?: string | null;
    booking?: {
      deceased: Array<{
        first_name: string;
        surname: string;
      }>;
    };
  };
  crewMembers?: Array<{
    crew_member: {
      user: {
        first_name: string;
        last_name: string;
      };
    };
    assignment?: {
      role?: string | null;
      notes?: string;
    }[];
  }>;
  thumbnailUrl?: string;
}

const YouTubeModal: React.FC<IGenerateYouTubeLinkDialogProps> = ({
  open,
  onClose,
  streamData: propStreamData,
  crewMembers = [],
  onSuccess,
  thumbnailUrl: propThumbnailUrl,
  selectedChannel,
}) => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<IStreamData>>({});
  const [isUpdate, setIsUpdate] = useState(false);

  const formatCrewDescription = useCallback((crew: typeof crewMembers) => {
    if (!crew?.length) {
      return "";
    }

    const roleOrder = [
      "Executive Producer",
      "Producer",
      "Assistant Producer",
      "Liveswitcher",
    ];

    const sortedCrew = [...crew].sort((a, b) => {
      const roleA = a.assignment?.[0]?.role || "Crew Member";
      const roleB = b.assignment?.[0]?.role || "Crew Member";

      const indexA = roleOrder.indexOf(roleA);
      const indexB = roleOrder.indexOf(roleB);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      if (indexA !== -1) {
        return -1;
      }

      if (indexB !== -1) {
        return 1;
      }
      return 0;
    });

    return sortedCrew
      .map((member) => {
        const fullName = `${member.crew_member.user.first_name} ${member.crew_member.user.last_name}`;
        const role = member.assignment?.[0]?.role || "Crew Member";
        return `${role} - ${fullName}`;
      })
      .join("\n");
  }, []);
  const formatStreamTitle = useCallback(
    (streamData: typeof propStreamData) =>
      streamData?.booking?.deceased?.[0]
        ? `Celebrating the life of ${streamData.booking.deceased[0].first_name} ${streamData.booking.deceased[0].surname}`
        : streamData?.title || "Live Stream",
    []
  );

  const formatDescription = useCallback(
    (
      streamData: typeof propStreamData,
      crew: typeof crewMembers,
      selectedChannel?: { name?: string } | null
    ) => {
    const channelName = selectedChannel?.name || "";

    const venueName =
      streamData?.venue_name && streamData.venue_name !== "N/A"
        ? streamData.venue_name
        : streamData?.service?.venue?.name &&
            streamData.service.venue.name !== "N/A"
          ? streamData.service.venue.name
          : streamData?.event_details?.venue?.name || "Venue TBC";

    const address = [
      streamData?.address_one,
      streamData?.address_two,
      streamData?.suburb,
      streamData?.city,
    ]
      .filter((part) => part && part !== "N/A")
      .join(", ");

    return `${channelName}
Broadcasting live from ${venueName}, ${address}
${formatCrewDescription(crew)}`;
  },
  [formatCrewDescription]
  );

  const formatScheduledTime = useCallback(
    (streamData: typeof propStreamData) =>
      streamData?.date && streamData?.start
        ? `${streamData.date}T${streamData.start}`.slice(0, 16)
        : new Date().toISOString().slice(0, 16),
    []
  );

  const [streamData, setStreamData] = useState<IStreamData>({
    streamTitle: "",
    description: "",
    thumbnailUrl: propThumbnailUrl || "",
    scheduledStartTime: "",
    privacySetting: "Public",
  });

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>(
    propThumbnailUrl || ""
  );

  useEffect(() => {
    if (propStreamData) {
      setStreamData({
        streamTitle: formatStreamTitle(propStreamData),
        description: formatDescription(propStreamData, crewMembers),
        thumbnailUrl: propThumbnailUrl || "",
        scheduledStartTime: formatScheduledTime(propStreamData),
        privacySetting: "Public",
      });
      setThumbnailPreview(propThumbnailUrl || "");
      setErrors({});
      setIsUpdate(!!propStreamData.youtube_stream_link);
    }
  }, [
    crewMembers,
    formatDescription,
    formatScheduledTime,
    formatStreamTitle,
    propStreamData,
    propThumbnailUrl,
  ]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setStreamData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleInputChange = (field: string, value: string) => {
    setStreamData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!["image/png", "image/jpeg"].includes(file.type))
        return showErrorToast("Only PNG or JPEG files allowed", "thumb-type");
      if (file.size > 2 * 1024 * 1024)
        return showErrorToast("Thumbnail must be under 2MB", "thumb-size");
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<IStreamData> = {};
    if (!streamData.streamTitle)
      newErrors.streamTitle = "Stream title required";
    if (!streamData.description) newErrors.description = "Description required";
    if (!streamData.scheduledStartTime)
      newErrors.scheduledStartTime = "Start time required";
    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return showErrorToast("Fill all fields", "form-error");
    setLoading(true);

    const formData = new FormData();
    formData.append("title", streamData.streamTitle);
    formData.append("description", streamData.description);
    formData.append("privacy_status", streamData.privacySetting.toLowerCase());
    formData.append("resolution", "720p");
    formData.append("frame_rate", "30fps");
    formData.append(
      "scheduled_start_time",
      `${streamData.scheduledStartTime}:00Z`
    );
    if (thumbnailFile)
      formData.append("thumbnail", thumbnailFile, thumbnailFile.name);
    else if (streamData.thumbnailUrl)
      formData.append("thumbnail_url", streamData.thumbnailUrl);

    try {
      const endpoint = isUpdate
        ? `/api/streams/stream/${id}/update-youtube-stream/`
        : `/api/streams/stream/${id}/create-youtube-stream/`;

      const method = isUpdate ? "put" : "post";
      await apiService[method](endpoint, formData);

      showSuccessToast(
        isUpdate
          ? "YouTube link updated successfully!"
          : "YouTube link generated successfully!"
      );
      onSuccess();
    } catch (error) {
      const errorMessage =
        (error as { response?: { data?: { detail?: string; error?: string } } })
          ?.response?.data?.detail ||
        (error as { response?: { data?: { detail?: string; error?: string } } })
          ?.response?.data?.error ||
        (error as { message?: string })?.message ||
        "Something went wrong.";
      showErrorToast(errorMessage, "youtube-link-error");
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.5 } }}
      >
        <Box display="flex" justifyContent="space-between">
          <DialogTitle sx={{ color: colors.primary[300] }}>
            {isUpdate ? "Update YouTube Link" : "Generate YouTube Link"}
            {selectedChannel && (
              <Typography sx={{ display: "block", color: colors.primary[100] }}>
                Channel: {selectedChannel.name}
              </Typography>
            )}
          </DialogTitle>
          <IconButton onClick={onClose} disabled={loading}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <CustomTextField
            label="Stream Title"
            name="streamTitle"
            value={streamData.streamTitle}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={loading}
            error={!!errors.streamTitle}
            helperText={errors.streamTitle}
          />
          <CustomTextField
            label="Description"
            name="description"
            value={streamData.description}
            onChange={handleChange}
            multiline
            rows={8}
            fullWidth
            margin="normal"
            required
            disabled={loading}
            error={!!errors.description}
            helperText={errors.description}
            sx={{
              "& .MuiInputBase-root": { height: "auto", minHeight: "120px" },
            }}
          />
          <Box my={2}>
            <Typography variant="subtitle1">Thumbnail</Typography>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleThumbnailChange}
              disabled={loading}
            />
            <Box
              mt={1}
              width={150}
              height={84}
              display="flex"
              alignItems="center"
              justifyContent="center"
              border={`1px solid ${colors.primary[300]}`}
              borderRadius={1}
            >
              {thumbnailPreview ? (
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Typography variant="caption">
                  {" "}
                  Manually Upload Thumbnail
                </Typography>
              )}
            </Box>
          </Box>
          <CustomTextField
            fullWidth
            type="datetime-local"
            label="Scheduled Start Time"
            value={streamData.scheduledStartTime}
            onChange={(e) =>
              setStreamData((prev) => ({
                ...prev,
                scheduledStartTime: e.target.value,
              }))
            }
            InputLabelProps={{ shrink: true }}
            required
            disabled={loading}
            error={!!errors.scheduledStartTime}
            helperText={errors.scheduledStartTime}
          />
          <FormControl fullWidth margin="normal" required>
            <CustomSelect
              labelId="privacy-setting-label"
              id="privacy-setting"
              name="privacySetting"
              value={streamData.privacySetting}
              label="Privacy Setting"
              disabled={loading}
              onChange={(e) =>
                handleInputChange("privacySetting", e.target.value as string)
              }
            >
              <MenuItem value="Public">Public</MenuItem>
              <MenuItem value="Unlisted">Unlisted</MenuItem>
              <MenuItem value="Private">Private</MenuItem>
            </CustomSelect>
          </FormControl>
          <Box display="flex" justifyContent="center" mt={3}>
            <motion.div
              initial={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{
                  backgroundColor: colors.grey[300],
                  "&:hover": { backgroundColor: colors.grey[400] },
                }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isUpdate ? (
                  "UPDATE LINK"
                ) : (
                  "GENERATE LINK"
                )}
              </Button>
            </motion.div>
          </Box>
        </DialogContent>
      </motion.div>
    </Dialog>
  );
};

export default YouTubeModal;
